import Moment from 'moment'
import { array, number, object, setLocale, string, StringSchema } from 'yup'

import { Request, PolicyRule, PolicyRequestGeneralRule } from 'src/types'
import { stringify } from 'src/core/hooks/use-translation'

type GetValidationSchemaProps = {
  policyRule: PolicyRule
}

setLocale({
  mixed: {
    default: 'common.required',
    notType: 'common.required',
    required: 'common.required'
  }
})

export function getInitialValues(): Request {
  return {
    contract_id: '',
    cost: 0,
    date_end: Moment()
      .hours(0)
      .minutes(0)
      .seconds(0)
      .millisecond(0)
      .add(1, 'day')
      .unix(),
    full_request_air: [],
    full_request_hotel: [],
    full_request_vehicle: [],
    justification: '',
    obs: '',
    organization_id: '',
    request_custom_data: [],
    request_id: '',
    request_itineraries: [],
    request_number: '',
    request_standard_assignments: {
      request_standard_billings: [],
      request_standard_sharings: []
    },
    request_tickets: [],
    request_type: 'TRIP',
    request_user_data: [],
    requested_on: Moment().hours(0).minutes(0).seconds(0).millisecond(0).unix(),
    requester: null,
    status: 'APPROVAL_PENDING',
    ticket_types: [],
    title: '',
    tmp_reason_id: ''
  }
}

function getValidationMinimumDaysLimitJustification(
  policy: PolicyRequestGeneralRule,
  request_on: number
) {
  const { action, active, minimum_days_limit, value } = policy || {}

  const required = string()
    .required()
    .trim()
    .max(
      1024,
      stringify('web.setup.form.field-title.max-character-error', {
        value: 1024
      })
    )

  if (active && minimum_days_limit && !value) {
    return required
  }

  if (active && action === 'ALERT' && minimum_days_limit) {
    const deadLine = Moment()
      .add(value, 'days')
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    const momentOfRequest_on = Moment.unix(request_on).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    })

    if (momentOfRequest_on.unix() < deadLine.unix()) {
      return required
    }
  }
  return string().notRequired()
}

function getValidationMinimumDaysLimitRequestedOn(
  policy: PolicyRequestGeneralRule
) {
  const { action, active, minimum_days_limit, value } = policy || {}

  if (active && minimum_days_limit && !value) {
    return number().required()
  }

  if (active && action === 'BLOCK' && minimum_days_limit) {
    const nexDay = Moment()
      .add(value, 'days')
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    return number()
      .required()
      .min(
        nexDay.unix(),
        stringify('web.setup.form.start-date.minimum-days-limit', {
          value: nexDay.format('DD/MM/YYYY')
        })
      )
  }
  return number().required()
}

export function getValidationSchema({ policyRule }: GetValidationSchemaProps) {
  return object().shape({
    request_custom_data: array()
      .of(
        object().shape({
          contract_id: string().when(
            'active',
            (active: boolean, schema: StringSchema<string>) => {
              if (active && !policyRule?.enable_def_custom_contract) {
                return schema.required().typeError()
              }
              return schema
            }
          ),
          target_id: string().when(
            'active',
            (active: boolean, schema: StringSchema<string>) => {
              if (active && !policyRule?.enable_def_custom_target) {
                return schema.required().typeError()
              }
              return schema
            }
          )
        })
      )
      .test(
        'enable_enforce_additional_info',
        'web.setup.additional-information.required',
        (values: any) =>
          !(policyRule?.enable_enforce_additional_info && values.length <= 0)
      ),
    request_user_data: array().min(
      1,
      'web.setup.participants.add-some-participant'
    ),
    justification: string().when(
      'requested_on',
      (requested_on: number, schema: StringSchema<string>) =>
        getValidationMinimumDaysLimitJustification(
          policyRule?.policy_request_general_rule,
          requested_on
        )
    ),
    requested_on: getValidationMinimumDaysLimitRequestedOn(
      policyRule?.policy_request_general_rule
    ),
    title: string()
      .trim()
      .max(
        50,
        stringify('web.setup.form.field-title.max-character-error', {
          value: 50
        })
      )
      .required()
  })
}
